'use client';

import { createContext, useCallback, useContext, useEffect, useId, useMemo, useState, type ReactNode } from 'react';

/**
 * 스택 기반 자동 z-index 배정 기준값.
 *
 * - 스택의 첫 모달이 `BASE_Z_INDEX`, 이후 열리는 모달마다 `Z_INDEX_STEP` 만큼 증가한다.
 * - 상한(`z-[1040]`)에는 모달 위에 떠야 하는 포털 요소(`Select` 의 옵션 목록 등)가 있으므로,
 *   현실적인 중첩 깊이에서도 그 아래에 머물도록 STEP 을 작게 둔다(1000 기준 19단계까지 1040 미만).
 */
const BASE_Z_INDEX = 1000;
const Z_INDEX_STEP = 2;

export type ModalZIndex = 'base' | 'overlay' | 'dropdown' | 'popover' | 'top';

const Z_INDEX_VALUES: Record<ModalZIndex, number | null> = {
  base: null,
  overlay: 1000,
  dropdown: 1010,
  popover: 1020,
  top: 1030,
};

type ModalStackEntry = {
  id: string;
  zIndexProp: ModalZIndex;
};

type ModalStackContextValue = {
  register: (id: string, zIndexProp: ModalZIndex) => void;
  unregister: (id: string) => void;
  getStackInfo: (id: string, currentZIndexProp: ModalZIndex) => { zIndex: number; depth: number };
  size: number;
};

const ModalStackContext = createContext<ModalStackContextValue | null>(null);

/**
 * 활성 모달의 열림 순서와 zIndexProp 을 보관하는 컨텍스트.
 *
 * 나중에 열린 모달이 스택 뒤쪽(=더 높은 z-index)에 위치하므로,
 * "나중에 연 모달이 항상 위에 온다"는 규칙이 호출처 개입 없이 자동으로 보장된다.
 * 클라이언트 전용 컴포넌트다(서버 컴포넌트에서 import 금지).
 */
export const ModalStackProvider = ({ children }: { children: ReactNode }) => {
  const [stack, setStack] = useState<ModalStackEntry[]>([]);

  const register = useCallback((id: string, zIndexProp: ModalZIndex) => {
    setStack((prev) => {
      const exists = prev.some((entry) => entry.id === id);
      if (exists) {
        return prev.map((entry) => (entry.id === id ? { ...entry, zIndexProp } : entry));
      }
      return [...prev, { id, zIndexProp }];
    });
  }, []);

  const unregister = useCallback((id: string) => {
    setStack((prev) => prev.filter((entry) => entry.id !== id));
  }, []);

  const getStackInfo = useCallback(
    (id: string, currentZIndexProp: ModalZIndex) => {
      const index = stack.findIndex((entry) => entry.id === id);
      const position = index < 0 ? stack.length : index;

      let currentZIndex = BASE_Z_INDEX;
      for (let i = 0; i <= position; i++) {
        const entryZIndexProp = i === position ? currentZIndexProp : (stack[i]?.zIndexProp ?? 'base');
        const explicitVal = Z_INDEX_VALUES[entryZIndexProp];
        if (explicitVal !== null) {
          currentZIndex = Math.max(currentZIndex, explicitVal);
        }
        if (i > 0) {
          currentZIndex += Z_INDEX_STEP;
        }
      }

      return {
        zIndex: currentZIndex,
        depth: position,
      };
    },
    [stack],
  );

  const value = useMemo<ModalStackContextValue>(
    () => ({
      register,
      unregister,
      getStackInfo,
      size: stack.length,
    }),
    [register, unregister, getStackInfo, stack.length],
  );

  return <ModalStackContext.Provider value={value}>{children}</ModalStackContext.Provider>;
};

export type ModalStackInfo = {
  zIndex: number;
  depth: number;
};

/**
 * 모달이 열려 있는 동안 스택에 자신을 등록하고, 스택 위치 기반 z-index 와 depth 를 반환한다.
 *
 * - Provider 가 마운트되지 않은 환경에서는 `null` 을 반환한다. 호출 측(`BaseModal`)은
 *   이때 기존 `zIndexMap` 클래스 동작으로 폴백한다.
 * - 열려 있지 않으면(`isOpen=false`) `null` 을 반환하고 스택에서 빠진다.
 *
 * @param isOpen 모달이 화면에 떠 있는지 여부(숨김 상태면 false 로 넘긴다).
 * @param zIndexProp 모달의 zIndex 레벨 (기본값 'base').
 */
export const useModalStack = (isOpen: boolean, zIndexProp: ModalZIndex = 'base'): ModalStackInfo | null => {
  const context = useContext(ModalStackContext);
  const id = useId();

  const register = context?.register;
  const unregister = context?.unregister;

  useEffect(() => {
    if (!register || !unregister || !isOpen) {
      return;
    }
    register(id, zIndexProp);
    return () => unregister(id);
  }, [register, unregister, isOpen, id, zIndexProp]);

  if (!context || !isOpen) {
    return null;
  }

  return context.getStackInfo(id, zIndexProp);
};

/**
 * useModalStack 을 래핑하여 z-index 만 필요한 기존 호출처를 지원한다.
 */
export const useModalStackZIndex = (isOpen: boolean, zIndexProp: ModalZIndex = 'base'): number | null => {
  const info = useModalStack(isOpen, zIndexProp);
  return info ? info.zIndex : null;
};
